
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable, inject } from "@angular/core";
import { TaskState } from "../store/reducers/tasks.reducers";
import { Store } from "@ngrx/store";
import { getAuthUser } from "../store/selectors";
import { AuthService } from "../../common/providers/auth.service";
import { Observable, Subject, combineLatest, forkJoin } from "rxjs";
import { map, take } from "rxjs/operators";
import { CommonUtil, mobileonly } from "../../common/utils/common.utils";
import { Task, TaskComment, ICompact, Project, User, List, PendingOperation, Tag, SearchQuery, Location } from "../models";
import { AppConstants } from "../../common/utils/app-constants";
import { DatabaseReady } from "../../actions/app";
import { TaskUtils } from "../shared/task-utils";
import { LocalStorageService } from "./local-storage.service";
import { Broadcaster } from "../../common/providers/broadcaster.service";
import { FilesStorageService } from "./files-storage.service";
import { environment } from "../../../environments/environment";
import { IndexedDBService } from "./db/indexedDB.service";
import { StorageService } from "../../common/services/storage.service";


import { DatabaseCoreLayer } from "./database-core.layer";

// Mechanical decomposition layer of DatabaseService (Q-02). Behavior-preserving.
export abstract class DatabaseOpsLayer extends DatabaseCoreLayer {
  public deletePendingOperation(taskId: number): Observable<boolean>{
    console.log("[SQLITE] deletePendingOperation, taskId: " + taskId);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] deletePendingOperation skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<boolean>();

    this.sqliteDB.transaction((tx) => {
      tx.executeSql(this.DELETE_PENDING_OPERATION_SQL, [taskId],
        (tx, result) => {
          console.log("[SQLITE] deletePendingOperation, success result: ", result);
          response.next(true);
        }, (tx, error) => {
          console.error("[SQLITE] deletePendingOperation, error: ", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] deletePendingOperation: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] deletePendingOperation: Transaction success.`);
    });

    return response.asObservable().pipe(take(1));
  }
  ///

  cleanAllData(): Observable<any> {
    console.log("[SQLITE] cleanAllData");

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] cleanAllData skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    this.sqliteDB.sqlBatch([
      this.DELETE_TASKS,
      this.DELETE_AVATARS,
      this.DELETE_PROJECTS,
      this.DELETE_LISTS,
      this.DELETE_LOCATIONS,
      this.DELETE_TAGS,
      this.DELETE_TAGS_TASKS,
      this.DELETE_PENDING_OPS,
      this.DELETE_SAVE_SEARCHES
    ], () => {
      console.log(`[SQLITE] cleanAllData: Transaction success`);
      response.next(true);
    }, (error) => {
      console.error("[SQLITE] cleanAllData: Transaction error", error);
      response.next(false);
    });

    return response.asObservable().pipe(take(1));
  }

  cleanTasksData(): Observable<any> {
    console.log("[SQLITE] cleanTasksData");

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] cleanTasksData skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    this.sqliteDB.transaction((tx) => {
      tx.executeSql(this.DELETE_TASKS, [],
        (tx, result) => {
          console.log("[SQLITE] cleanTasksData, success result: ", result);
          response.next(true);
        }, (tx, error) => {
          console.error("[SQLITE] cleanTasksData, error: ", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] cleanTasksData: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] cleanTasksData: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  close(): Observable<any> {
    console.log("[SQLITE] close");

    const response = new Subject();

    try {
      if (this.sqliteDB) {
        this.sqliteDB.close(() => {
          console.log("[SQLITE] SQLite DB is closed!");
          response.next("");
        }, (error) => {
          console.log("[SQLITE] Error closing DB:" + error.message);
          response.next("");
        });
      }
    } catch (e) {
      console.log(`[SQLITE] close, error has occured - ${e}`);
      response.next("");
    }

    return response.asObservable().pipe(take(1));
  }

  deleteDatabase(): Observable<any> {
    console.log("[SQLITE] deleteDatabase");

    const response = new Subject();

    try {
      this.close().subscribe(() => {
        if (CommonUtil.isOnNativeMobileDevice()) {
          window.sqlitePlugin.deleteDatabase({
            name: this.KEY_SQLITE_DB,
            location: "default",
            androidDatabaseImplementation: 2
          }, () => {
            console.log("[SQLITE] Database deleted");
            response.next("");
          }, (error) => {
            console.log(" [SQLITE] Delete database ERROR: " + JSON.stringify(error));
            response.next("");
          });
        } else if (environment.isElectron) {
          try {
            this.sqliteDB.deleteDatabase(this.KEY_SQLITE_DB, (err) => {
              if (err){
                console.error("[SQLITE] Delete database ERROR: " + JSON.stringify(err));
                response.error(err);
              } else {
                console.log("[SQLITE] sqliteDB deleted");
                response.next("");
              }
            });
          } catch (e) {
            console.log(`[SQLITE] Error has occured when delete database - ${e}`);
            response.error(e);
          }
        }
      });
    } catch (e) {
      console.log(`[SQLITE] Error has occured - ${e}`);
      response.next("");
    }
    return response.asObservable().pipe(take(1));
  }

  assignAvatars(uniqueUsersIds: any[], serverUrls: string[], forceResync: boolean, avatarPropertyName: string): Observable<any> {
    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE][assignAvatars] skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    console.log("[SQLITE][assignAvatars] uniqueUserIds", uniqueUsersIds.length, serverUrls);

    // 1. check if users avs are already cached
    this.findLocalAvatarsNames(uniqueUsersIds).subscribe((localAvatarsNamesHash) => {
      console.log("[SQLITE][assignAvatars] findLocalAvatarsNames localAvatarsNamesHash", localAvatarsNamesHash);

      let opsDone = 0;
      let resultsHash = {};

      if (uniqueUsersIds.length > 0) {
        uniqueUsersIds.forEach((userId, idx) => {

          let localFileName = localAvatarsNamesHash[userId];
          console.log("[SQLITE][assignAvatars] userId & localFileName", userId, localFileName);

          // exists, so assign local url
          if (localFileName && !forceResync) {
            if (CommonUtil.isOnIOS()) {
              this.buildReadableIOSLocalAvatarUrl(this.filesStorageService.filePathOnDisc(localFileName)).subscribe((readableLocalFileUrl) => {
                if (readableLocalFileUrl) {
                  resultsHash[userId] = readableLocalFileUrl;
                }

                ++opsDone;
                if (opsDone === uniqueUsersIds.length) {
                  console.log("[SQLITE][assignAvatars] DONE", opsDone, resultsHash);
                  response.next(resultsHash);
                }
              });
            } else {
              const readableLocalFileUrl = this.filesStorageService.filePathOnDisc(localFileName);
              resultsHash[userId] = readableLocalFileUrl;

              ++opsDone;
              if (opsDone === uniqueUsersIds.length) {
                console.log("[SQLITE][assignAvatars] DONE", opsDone, resultsHash);
                response.next(resultsHash);
              }
            }

          // not exists, so download, save & assign
          } else {
            const recordAlreadyExists = localFileName ? true : false;

            const currentTimestamp = new Date().getTime();
            localFileName = `user_avatar_${userId}_${currentTimestamp}.png`;

            this.downloadAvatarToDisk(serverUrls[idx], localFileName).subscribe(localFileUrl => {
              if (localFileUrl) {
                this.saveAvatar(recordAlreadyExists, localFileName, userId, currentTimestamp).subscribe(success => {

                });
                resultsHash[userId] = localFileUrl;
              } else {
                resultsHash[userId] = serverUrls[idx];
                if (recordAlreadyExists) {
                  this.deleteAvatar(userId).subscribe( success => {
                  });
                }
              }

              ++opsDone;
              if (opsDone === uniqueUsersIds.length) {
                console.log("[SQLITE][assignAvatars] DONE", opsDone, resultsHash);
                response.next(resultsHash);
              }

            });
          }
        });
      } else {
        console.log("[SQLITE][assignAvatars] DONE", opsDone, resultsHash);
        response.next(resultsHash);
      }

    });
    return response.asObservable().pipe(take(1));
  }

  protected buildReadableIOSLocalAvatarUrl(localFileUrl: String): Observable<any> {
    console.log("[SQLITE][buildReadableIOSLocalAvatarUrl]", localFileUrl);

    const response = new Subject<any>();

    window.resolveLocalFileSystemURL(localFileUrl, (fileEntry) => {
      fileEntry.file((file) => {
        const reader = new FileReader();
        reader.onloadend = function (e) {
          localFileUrl = this.result as string;
          response.next(localFileUrl);
        };
        reader.readAsDataURL(file);
      }, (err) => {
        console.error("[SQLIte][buildReadableLocalAvatarUrl] FileReader error", err);
        response.next(null);
        // TODO: maybe re-load from server?
      });
    }, (err) => {
      console.error("[SQLIte][buildReadableLocalAvatarUrl] resolveLocalFileSystemURL error", err);
      response.next(null);
      // TODO: maybe re-load from server?
    });

    return response.asObservable().pipe(take(1));
  }

  protected downloadAvatarToDisk(serverUrl: String, localFileName: String): Observable<any> {
    const response = new Subject<any>();

    this.filesStorageService.downloadImageToDevice(serverUrl, localFileName)
      .subscribe(localUrl => {
        response.next(localUrl);
      }, err => {
        console.error("[SQLITE] downloadAvatarToDisk error", err);
        response.next(null);
      }
    );

    return response.asObservable().pipe(take(1));
  }

  protected findLocalAvatarsNames(userIds: number[]): Observable<any> {
    console.log("[SQLITE] findLocalAvatarsNames", userIds);

    const response = new Subject<any>();

    this.sqliteDB.readTransaction((tFun) => {
      const query = this.FIND_AVATARS_BY_USER_ID_SQL + "('" + userIds.join("','") + "')";
      console.log("[SQLITE] findLocalAvatarsNames query", query);
      tFun.executeSql(query, [],
        (tx, result) => {
          console.log("[SQLITE] findLocalAvatarsNames found", result.rows.length);
          let avatars = {};
          if (result.rows.length > 0) {
            for (let i = 0; i < result.rows.length; i++) {
              const item = result.rows.item(i);
              avatars[item.user_id] = item.local_file_name;
            }
          }
          response.next(avatars);
        }, (tx, error) => {
          console.error("[SQLITE] findLocalAvatarsNames: error", error);
        }
      );
    });

    return response.asObservable().pipe(take(1));
  }

  protected saveAvatar(recordExists: boolean, fileName: String, userId: number, currentTimestamp: number): Observable<any> {
    console.log("[SQLITE] saveAvatar", userId, fileName, recordExists);

    const response = new Subject<any>();

    this.sqliteDB.transaction((tFun) => {
      const query = recordExists ? this.UPDATE_AVATAR_SQL : this.CREATE_AVATAR_IF_NOT_EXIST_SQL;
      const params = recordExists ? [fileName, currentTimestamp, userId] : [userId, fileName, currentTimestamp, userId];

      tFun.executeSql(query, params,
        (tx, result) => {
          console.log("[SQLITE] saveAvatar success");
          response.next(true);
        }, (tx, error) => {
          console.error("[SQLITE] saveAvatar: error", error);
          response.next(false);
        }
      );
    });

    return response.asObservable().pipe(take(1));
  }

  public deleteAvatar(id: number): Observable<boolean>{
    console.log("[SQLITE] deleteAvatar, id: " + id);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] deleteAvatar skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<boolean>();

    this.sqliteDB.transaction((tx) => {
      tx.executeSql(this.DELETE_AVATAR, [id],
        (tx, result) => {
          console.log("[SQLITE] deleteAvatar, success result: ", result);
          response.next(true);
        }, (tx, error) => {
          console.error("[SQLITE] deleteAvatar, error: ", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] deleteAvatar: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] deleteAvatar: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }
  /// Save Query

  public createOrUpdateSaveQuery(saveSearches: SearchQuery[]): Observable<boolean>{
    console.log("[SQLITE] createOrUpdateSaveQuery, lists: ", saveSearches);

    const response = new Subject<boolean>();

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] createOrUpdateSaveQuery skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const maybeExistentIds = saveSearches.map(query => query.id);

    let toCreateCounter = 0;
    let toUpdateCounter = 0;

    let time1;
    let time2;
    let time3;


    // Step 1:
    // find existing save searches
    time1 = performance.now();
    console.log("[SQLITE] createOrUpdateSaveQuery: Trying to accure transaction");
    this.sqliteDB.transaction((tx) => {
      console.log("[SQLITE] createOrUpdateSaveQuery: Transaction occured");
      time2 = performance.now();
      const query = this.FIND_SAVE_SEARCHES_IDS_BY_IDS_SQL + "('" + maybeExistentIds.join("','") + "')";

      tx.executeSql(query, [],
        (tx, result) => {
          console.log("[SQLITE] createOrUpdateSaveQuery found save searches in DB: ", result.rows.length);

          let existentIds = [];
          for (let i = 0; i < result.rows.length; i++) {
            let item = result.rows.item(i);
            existentIds.push(item.id);
          }

          // Step 2: insert/update tasks in DB
          //
          maybeExistentIds.forEach((id, index) => {
            let query;
            let params;

            const list = saveSearches[index];

            if (existentIds.includes(id)) {
              params = [list.name, list.created_on, list.issues_count, JSON.stringify(list.filters), list.id];
              query = this.UPDATE_SAVE_SEARCHES_SQL;
              ++toUpdateCounter;
            } else {
              params = [list.id, list.name, list.created_on, list.issues_count, JSON.stringify(list.filters)];
              query = this.CREATE_SAVE_SEARCHES_SQL;
              ++toCreateCounter;
            }

            // write to DB
            tx.executeSql(query, params, (tx, result) => {
              //
            }, (tx, error) => {
              console.error(`[SQLITE] createOrUpdateSaveQuery: Error has occured while saving task`, error);
            });
          });
          if (environment.isElectron) {
            response.next(true);
          }
        }, (tx, error) => {
          console.error("[SQLITE] createOrUpdateSaveQuery: Error has occured while finding tasks", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] createOrUpdateSaveQuery: Transaction error", error);
      response.next(false);
    }, () => {
      time3 = performance.now();
      console.log(`[SQLITE] createOrUpdateSaveQuery: Transaction success. Created: ${toCreateCounter}, Updated: ${toUpdateCounter}. TTAT ${(time2 - time1).toFixed()} TTFT ${(time3 - time2).toFixed()} Total ${(time3 - time1).toFixed()}`);
      response.next(true);
    });

    return response.asObservable().pipe(take(1));
  }

  public getSaveSearches(): Observable<SearchQuery[]>{
    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getSaveSearches skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    console.log("[SQLITE] getSaveSearches");

    const response = new Subject<SearchQuery[]>();

    this.sqliteDB.readTransaction((tx) => {
      tx.executeSql(this.SELECT_SAVE_SEARCHES, [],
        (tx, result) => {
          let saveSearches: SearchQuery[] = [];
          if (result.rows) {
            for (let i = 0; i < result.rows.length; i++) {
              let item = result.rows.item(i);
              let filters = JSON.parse(item.filters);
              item.filters = filters;
              saveSearches.push(new SearchQuery(item));
            }
          }
          console.log("[SQLITE] getSaveSearches allTags", saveSearches);
          response.next(saveSearches);
        }, (tx, error) => {
          console.error("[SQLITE] getSaveSearches Error has occured", error);
          response.next(null);
        }
      );
    }, (error) => {
      console.error("[SQLITE] getSaveSearches: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] getSaveSearches: Transaction success.`);
    });

    return response.asObservable().pipe(take(1));
  }

  /**
   * Applies any schema migration that has not been recorded yet.
   *
   * Replaces a commented-out version that had three problems:
   *   - it called cleanTasksData(), which DELETEs every cached task;
   *   - it counted results from an async subscribe inside a synchronous filter(),
   *     so the count was always 0 by the time it was checked;
   *   - on a fresh install the columns already exist (CREATE TABLE lists them), so
   *     every ALTER would fail and be retried on each startup.
   *
   * ADD COLUMN's realistic failure is "column already exists", so a migration is
   * recorded as applied either way and the store converges for both fresh and
   * pre-existing databases. No task data is touched.
   */
  protected applyMigrations(): Observable<any> {
    const response = new Subject<boolean>();

    this.getMigrationIds().subscribe(migrationIds => {
      const applied = migrationIds || [];
      const pending = this.MIGRATION_IDS.filter(item => !applied.find(id => id === item.id));

      if (pending.length === 0) {
        console.log("[SQLITE] applyMigrations: nothing pending");
        response.next(false);
        return;
      }

      console.log(`[SQLITE] applyMigrations: ${pending.length} pending`);
      let remaining = pending.length;
      pending.forEach(item => {
        // Run them one at a time; alterTableDetails records the id itself on success,
        // and reports false when the column is already present.
        this.alterTableDetails(item).subscribe(() => {
          remaining--;
          if (remaining === 0) {
            response.next(true);
          }
        });
      });
    }, error => {
      console.error("[SQLITE] applyMigrations", error);
      response.next(false);
    });

    return response.asObservable().pipe(take(1));
  }

  protected runPendingMigrations(): Observable<any> {
    return this.applyMigrations();
  }

  public getMigrationIds(): Observable<any[]>{
    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getMigrationIds skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    console.log("[SQLITE] getMigrationIds");

    const response = new Subject<any[]>();

    this.sqliteDB.readTransaction((tx) => {
      tx.executeSql(this.SELECT_MIGRATION_IDS_SQL, [],
        (tx, result) => {
          let migrationIds: any[] = [];
          if (result.rows) {
            for (let i = 0; i < result.rows.length; i++) {
              let item = result.rows.item(i);
              migrationIds.push(item.migration_id);
            }
          }
          console.log("[SQLITE] getMigrationIds", migrationIds);
          response.next(migrationIds);
        }, (tx, error) => {
          console.error("[SQLITE] getMigrationIds Error has occured", error);
          response.next([]);
        }
      );
    }, (error) => {
      console.error("[SQLITE] getMigrationIds: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] getMigrationIds: Transaction success.`);
    });

    return response.asObservable().pipe(take(1));
  }
// Indexed DB -started

  addContactInfo(info): Observable<any> {
    return this.dbServiceImpl.addContactInfo(info);
  }

  getContactInfo(email): Observable<any> {
    return this.dbServiceImpl.getContactInfo(email);
  }

  addTaskStats(stats): Observable<any> {
    return this.dbServiceImpl.addTaskStats(stats);
  }

  getAllStats(): Observable<any> {
    return this.dbServiceImpl.getAllStats();
  }

  getAvatarById(id): Observable<any> {
    return this.dbServiceImpl.getAvatarById(id);
  }

  addAvatar(avatar): Observable<any> {

    if (avatar?.id) {
      this.avatar[avatar.id] = avatar.data;
    }
    return this.dbServiceImpl.addAvatar(avatar);
  }

  getAllAvatar(): Observable<any> {
    return this.dbServiceImpl.getAllAvatar();
  }

  addAllTasks(task): Observable<any> {
    return this.dbServiceImpl.addAllTasks(task);
  }

  getAllTasks(): Observable<any> {
    return this.dbServiceImpl.getAllTasks();
  }

  addNewTasks(task): Observable<any> {
    return this.dbServiceImpl.addNewTasks(task);
  }

  getNewTasks(): Observable<any> {
    return this.dbServiceImpl.getNewTasks();
  }

  addOpenTasks(task): Observable<any> {
    return this.dbServiceImpl.addOpenTasks(task);
  }

  getOpenTasks(): Observable<any> {
    return this.dbServiceImpl.getOpenTasks();
  }

  addCompletedTasks(task): Observable<any> {
    return this.dbServiceImpl.addCompletedTasks(task);
  }

  getTaskDetailsById(id): Observable<any> {
    return this.dbServiceImpl.getTaskDetailsById(id);
  }

  getTaskById(id,store?): Observable<any> {
    return this.dbServiceImpl.getTaskById(id, store);
  }

  addBulkTasks(task,store?): Observable<any> {
    return this.dbServiceImpl.addBulkTasks(task, store);
  }

  deleteBulkTasks(task, store?): Observable<any> {
    return this.dbServiceImpl.deleteBulkTasks(task, store);
  }

  getCompletedTasks(): Observable<any> {
    return this.dbServiceImpl.getCompletedTasks();
  }

  addAssignedTasks(task): Observable<any> {
    return this.dbServiceImpl.addAssignedTasks(task);
  }

  getAssignedTasks(): Observable<any> {
    return this.dbServiceImpl.getAssignedTasks();
  }

  addCreatedTasks(task): Observable<any> {
    return this.dbServiceImpl.addCreatedTasks(task);
  }

  getCreatedTasks(): Observable<any> {
    return this.dbServiceImpl.getCreatedTasks();
  }

  addTodayTasks(task): Observable<any> {
    return this.dbServiceImpl.addTodayTasks(task);
  }

  getTodayTasks(): Observable<any> {
    return this.dbServiceImpl.getTodayTasks();
  }

  addWeekTasks(task): Observable<any> {
    return this.dbServiceImpl.addWeekTasks(task);
  }

  getWeekTasks(): Observable<any> {
    return this.dbServiceImpl.getWeekTasks();
  }

  addTomorrowTasks(task): Observable<any> {
    return this.dbServiceImpl.addTomorrowTasks(task);
  }

  getTomorrowTasks(): Observable<any> {
    return this.dbServiceImpl.getTomorrowTasks();
  }

  addOverdueTasks(task): Observable<any> {
    return this.dbServiceImpl.addOverdueTasks(task);
  }

  getOverdueTasks(): Observable<any> {
    return this.dbServiceImpl.getOverdueTasks();
  }

  addWatchTasks(task): Observable<any> {
    return this.dbServiceImpl.addWatchTasks(task);
  }

  getWatchTasks(): Observable<any> {
    return this.dbServiceImpl.getWatchTasks();
  }

  addTagsTasks(tag,task): Observable<any> {
    return this.dbServiceImpl.addTagsTasks(tag,task);
  }

  getTagsTasks(tag): Observable<any> {
    return this.dbServiceImpl.getTagsTasks(tag);
  }

  addListTasks(list, task): Observable<any> {
    return this.dbServiceImpl.addListTasks(list, task);
  }

  getListTasks(list): Observable<any> {
    return this.dbServiceImpl.getListTasks(list);
  }

  addLocationTasks(location, task): Observable<any> {
    return this.dbServiceImpl.addLocationTasks(location, task);
  }

  getLocationTasks(location): Observable<any> {
    return this.dbServiceImpl.getLocationTasks(location);
  }

  addContacts(contact): Observable<any> {
    return this.dbServiceImpl.addContacts(contact);
  }

  getContacts(): Observable<any> {
    return this.dbServiceImpl.getContacts();
  }
   getAvatarByID(id): Observable<any> {
    return this.dbServiceImpl.getAvatarByID(id);
  }

  addPendingOperationTask(task): Observable<any> {
    return this.dbServiceImpl.addPendingOperationTask(task);
  }

  getPendingOperationTask(): Observable<any> {
    return this.dbServiceImpl.getPendingOperationTask();
  }

  deletePendingOperations(id): Observable<any> {
    return this.dbServiceImpl.deletePendingOperations(id);
  }

}
